"use client";

import { useState } from "react";
import { ArrowRight, Box, Layers, Settings, ShieldCheck, Zap } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const periods = ["Architectural", "Industrial", "Structural", "Custom"];
const categories = ["Aluminum Profiles", "Hardware", "Finishing"];
const masterpieces = [{
  id: 1,
  name: "Premium Extrusion Series",
  meta: "GRADE 6063-T5 · HIGH PRECISION",
  description: "High-performance aluminum profiles engineered for structural integrity and architectural elegance in modern commercial facades.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1722809/img/aluminum_profile_series.webp",
  imageDetail: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1722809/img/aluminum_profile_detail.webp",
  period: "Architectural",
  category: "Aluminum Profiles",
  provenance: "Sourced directly from our manufacturing partners in China, these profiles undergo rigorous quality control in our Sharjah warehouse. Designed for high-load commercial applications, they meet international standards for durability and thermal efficiency.",
  condition: "New stock. Precision-cut to specification. Anodized finish available in multiple metallic shades. Ready for immediate bulk dispatch."
}, {
  id: 2,
  name: "Heavy-Duty Architectural Hardware",
  meta: "STAINLESS STEEL · GRADE 316",
  description: "Robust architectural hardware solutions designed for high-traffic commercial environments, ensuring longevity and smooth operation.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1722809/img/architectural_hardware.png",
  imageDetail: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1722809/img/hardware_detail.png",
  period: "Industrial",
  category: "Hardware",
  provenance: "Engineered for the demanding UAE climate, our hardware range is tested for corrosion resistance and mechanical stress. Distributed from our Ajman logistics hub to projects across the region.",
  condition: "Factory-sealed. Corrosion-resistant coating applied. Full technical documentation and installation guides included with every order."
}, {
  id: 3,
  name: "Custom Extrusion Solutions",
  meta: "BESPOKE DESIGN · PROJECT SPECIFIC",
  description: "Tailored aluminum extrusion profiles developed to meet unique architectural requirements and complex structural designs.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1722809/img/custom_extrusion.webp",
  imageDetail: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1722809/img/custom_extrusion_detail.webp",
  period: "Custom",
  category: "Aluminum Profiles",
  provenance: "Collaborative design process with our engineering team to translate your vision into precise aluminum reality. Manufactured in partnership with global leaders in extrusion technology.",
  condition: "Custom-manufactured to project specifications. Strict adherence to tolerance standards. Quality assurance reports provided upon delivery."
}, {
  id: 4,
  name: "Industrial Structural Beams",
  meta: "HIGH-STRENGTH · LOAD BEARING",
  description: "Heavy-duty structural aluminum beams designed for industrial frameworks and large-scale commercial construction projects.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1722809/img/structural_beams.png",
  imageDetail: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1722809/img/structural_beams_detail.webp",
  period: "Structural",
  category: "Aluminum Profiles",
  provenance: "Stocked in our Al Ain facility to support rapid deployment for regional infrastructure projects. Proven performance in high-stress environments.",
  condition: "Industrial grade. Standardized dimensions available. Surface treated for enhanced durability. Ready for immediate site delivery."
}, {
  id: 5,
  name: "Anodized Finishing Systems",
  meta: "PREMIUM FINISH · UV RESISTANT",
  description: "Advanced finishing solutions for aluminum profiles, providing superior protection against environmental factors and aesthetic appeal.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1722809/img/anodized_finish.webp",
  imageDetail: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1722809/img/anodized_detail.png",
  period: "Architectural",
  category: "Finishing",
  provenance: "Our finishing processes utilize state-of-the-art technology to ensure uniform color and long-lasting protection for all architectural components.",
  condition: "Uniform finish quality. UV-resistant properties. Available in a wide range of RAL colors and metallic textures."
}, {
  id: 6,
  name: "Facade Mounting Systems",
  meta: "MODULAR · PRECISION FIT",
  description: "Advanced mounting hardware for curtain wall systems, ensuring secure and efficient installation for high-rise commercial buildings.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1722809/img/facade_mounting.png",
  imageDetail: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1722809/img/facade_mounting_detail.webp",
  period: "Structural",
  category: "Hardware",
  provenance: "Designed for ease of installation and structural reliability, these systems are the backbone of modern facade engineering in the UAE.",
  condition: "Modular components. Precision-engineered for seamless integration. High-load capacity tested."
}];
export default function Portfolio() {
  const intro = useRevealOnScroll<HTMLDivElement>();
  const filters = useRevealOnScroll<HTMLDivElement>();
  const grid = useRevealOnScroll<HTMLDivElement>();
  const [selectedPeriods, setSelectedPeriods] = useState<string[]>([]);
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  const [sort, setSort] = useState("newly-restored");
  const togglePeriod = (period: string) => {
    setSelectedPeriods(prev => prev.includes(period) ? prev.filter(p => p !== period) : [...prev, period]);
  };
  const toggleCategory = (category: string) => {
    setSelectedCategories(prev => prev.includes(category) ? prev.filter(c => c !== category) : [...prev, category]);
  };
  return <section data-section-id="3032" id="wholesale-aluminum-portfolio" className="bg-background text-foreground pt-32 pb-24 px-6 md:px-12 lg:px-20 overflow-hidden">
      <div className="max-w-7xl mx-auto">
        <div ref={intro.ref} className={`transition-all duration-1000 ease-out ${intro.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-8 mb-20">
            <div className="max-w-3xl">
              <span className="font-mono text-[12px] tracking-[0.3em] uppercase text-primary mb-6 block">
                01 // Inventory & Specifications
              </span>
              <h1 className="font-default font-bold text-5xl md:text-7xl lg:text-8xl leading-[0.95] tracking-tighter mb-8">
                Industrial <br />
                <span className="text-primary">Precision.</span>
              </h1>
              <p className="font-default text-lg leading-relaxed text-muted-foreground max-w-xl">
                World Sky Building Materials Trading LLC provides premium wholesale aluminum profiles and architectural hardware. Engineered for contractors who demand reliability and fast logistics.
              </p>
            </div>
            <div className="hidden lg:block">
              <div className="flex items-center gap-4 text-muted-foreground font-mono text-xs tracking-widest uppercase">
                <span>Sharjah</span>
                <span className="text-primary">◆</span>
                <span>Ajman</span>
                <span className="text-primary">◆</span>
                <span>Al Ain</span>
              </div>
            </div>
          </div>
        </div>

        <div ref={filters.ref} className={`mb-16 transition-all duration-1000 ease-out delay-150 ${filters.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start border-y border-accent-metallic/30 py-10">
            <div className="lg:col-span-3">
              <span className="font-mono text-[11px] tracking-[0.25em] uppercase text-muted-foreground block mb-4">
                Filter by Application
              </span>
              <div className="flex flex-wrap gap-2">
                {periods.map((period, i) => <Button key={period} data-index={i} variant="outline" size="sm" onClick={() => togglePeriod(period)} className={`rounded-lg font-mono text-[10px] tracking-wider uppercase transition-all ${selectedPeriods.includes(period) ? "bg-primary text-primary-foreground border-primary" : "border-accent-metallic/40 hover:border-primary hover:text-primary"}`}>
                    {period}
                  </Button>)}
              </div>
            </div>

            <div className="lg:col-span-6">
              <span className="font-mono text-[11px] tracking-[0.25em] uppercase text-muted-foreground block mb-4">
                Material Category
              </span>
              <div className="flex flex-wrap gap-2">
                {categories.map((category, i) => <Button key={category} data-index={i} variant="outline" size="sm" onClick={() => toggleCategory(category)} className={`rounded-lg font-mono text-[10px] tracking-wider uppercase transition-all ${selectedCategories.includes(category) ? "bg-primary text-primary-foreground border-primary" : "border-accent-metallic/40 hover:border-primary hover:text-primary"}`}>
                    {category}
                  </Button>)}
              </div>
            </div>

            <div className="lg:col-span-3">
              <span className="font-mono text-[11px] tracking-[0.25em] uppercase text-muted-foreground block mb-4">
                Sort Inventory
              </span>
              <Select value={sort} onValueChange={setSort}>
                <SelectTrigger className="w-full rounded-lg border-accent-metallic/40 bg-card/50 font-mono text-[11px] tracking-wider uppercase">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-card border-accent-metallic/40">
                  <SelectItem value="newly-restored">Latest Arrivals</SelectItem>
                  <SelectItem value="provenance">Popularity</SelectItem>
                  <SelectItem value="price">Stock Status</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>

        <div ref={grid.ref} className={`grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-accent-metallic/20 border border-accent-metallic/20 transition-all duration-1000 ease-out delay-300 ${grid.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          {masterpieces.map((piece, i) => <article key={piece.id} data-index={i} className="group bg-background p-8 flex flex-col transition-colors duration-500 hover:bg-card/30 relative">
              <div className="absolute top-0 left-0 w-full h-1 bg-primary scale-x-0 group-hover:scale-x-100 transition-transform duration-500 origin-left" />
              
              <div className="relative mb-8 overflow-hidden aspect-[4/5] rounded-lg">
                <img src={piece.image} data-wvc-srcset={`${piece.image}-wvc-srcset`} data-wvc-sizes={`${piece.image}-wvc-sizes`} alt={piece.name} loading="lazy" decoding="async" className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-110" />
                <div className="absolute inset-0 bg-primary/10 opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
              </div>

              <div className="flex items-center gap-2 mb-4">
                <span className="font-mono text-[10px] tracking-[0.2em] uppercase text-primary">
                  {piece.category}
                </span>
                <Separator className="w-4 bg-accent-metallic/40" />
                <span className="font-mono text-[10px] tracking-[0.2em] uppercase text-muted-foreground">
                  {piece.period}
                </span>
              </div>

              <h3 className="font-default font-bold text-2xl leading-tight text-foreground mb-3 group-hover:text-primary transition-colors">
                {piece.name}
              </h3>

              <p className="font-mono text-[11px] tracking-[0.1em] uppercase text-muted-foreground/70 mb-6">
                {piece.meta}
              </p>

              <p className="font-default text-sm leading-relaxed text-muted-foreground mb-8 line-clamp-3">
                {piece.description}
              </p>

              <div className="mt-auto pt-6 border-t border-accent-metallic/10 flex flex-col gap-4">
                <Link to="/ConnectWithSalesPage" className="group/btn inline-flex items-center justify-between w-full bg-primary text-primary-foreground px-6 py-3 rounded-lg font-mono text-[11px] tracking-[0.2em] uppercase transition-all hover:bg-primary/90">
                  Request Quote
                  <ArrowRight className="w-4 h-4 transition-transform group-hover/btn:translate-x-1" />
                </Link>

                <Dialog>
                  <DialogTrigger asChild>
                    <Button variant="ghost" className="w-full rounded-lg border border-accent-metallic/20 font-mono text-[10px] tracking-[0.2em] uppercase text-muted-foreground hover:text-primary hover:border-primary transition-all">
                      Technical Specs
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="bg-card text-card-foreground border-accent-metallic/30 max-w-2xl rounded-lg">
                    <DialogHeader className="text-left">
                      <div className="flex items-center gap-2 mb-4">
                        <Box className="w-4 h-4 text-primary" />
                        <span className="font-mono text-[11px] tracking-[0.3em] uppercase text-primary">
                          Technical Data Sheet
                        </span>
                      </div>
                      <DialogTitle className="font-default font-bold text-4xl tracking-tighter text-foreground">
                        {piece.name}
                      </DialogTitle>
                      <p className="font-mono text-[12px] tracking-[0.2em] uppercase text-muted-foreground mt-2">
                        {piece.meta}
                      </p>
                    </DialogHeader>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mt-8">
                      <div className="space-y-6">
                        <div className="flex gap-4">
                          <Layers className="w-5 h-5 text-primary shrink-0" />
                          <div>
                            <h4 className="font-mono text-[11px] tracking-[0.2em] uppercase text-foreground mb-2">
                              Provenance & Supply
                            </h4>
                            <DialogDescription className="font-default text-sm leading-relaxed text-muted-foreground">
                              {piece.provenance}
                            </DialogDescription>
                          </div>
                        </div>
                        <div className="flex gap-4">
                          <Settings className="w-5 h-5 text-primary shrink-0" />
                          <div>
                            <h4 className="font-mono text-[11px] tracking-[0.2em] uppercase text-foreground mb-2">
                              Technical Condition
                            </h4>
                            <p className="font-default text-sm leading-relaxed text-muted-foreground">
                              {piece.condition}
                            </p>
                          </div>
                        </div>
                      </div>
                      <div className="space-y-6">
                        <div className="bg-background/50 p-6 rounded-lg border border-accent-metallic/10">
                          <h4 className="font-mono text-[11px] tracking-[0.2em] uppercase text-primary mb-4 flex items-center gap-2">
                            <ShieldCheck className="w-4 h-4" />
                            Quality Assurance
                          </h4>
                          <ul className="space-y-3 font-default text-xs text-muted-foreground">
                            <li className="flex items-center gap-2">
                              <Zap className="w-3 h-3 text-primary" />
                              ISO 9001 Certified Manufacturing
                            </li>
                            <li className="flex items-center gap-2">
                              <Zap className="w-3 h-3 text-primary" />
                              ASTM Standard Compliance
                            </li>
                            <li className="flex items-center gap-2">
                              <Zap className="w-3 h-3 text-primary" />
                              Rigid UAE Climate Testing
                            </li>
                          </ul>
                        </div>
                        <Button className="w-full bg-primary text-primary-foreground rounded-lg h-12 font-mono text-[11px] tracking-[0.2em] uppercase">
                          Inquire via WhatsApp
                          <ArrowRight className="w-4 h-4 ml-2" />
                        </Button>
                      </div>
                    </div>
                  </DialogContent>
                </Dialog>
              </div>
            </article>)}
        </div>

        <div className="mt-24 flex flex-col md:flex-row items-center justify-between gap-8 border-t border-accent-metallic/20 pt-12">
          <div className="flex items-center gap-6">
            {["01", "02", "03", "04"].map((num, i) => <Link key={num} data-index={i} to="#" className={`font-mono text-sm tracking-widest transition-colors duration-300 ${i === 0 ? "text-primary border-b-2 border-primary pb-1" : "text-muted-foreground hover:text-primary"}`}>
                {num}
              </Link>)}
          </div>
          <p className="font-mono text-[10px] tracking-[0.3em] uppercase text-muted-foreground/50">
            Showing 6 of 48 Industrial Components
          </p>
        </div>
      </div>
    </section>;
}